import React, { useEffect, useState } from "react";
import { PatientInfo } from "components";
import SwipeableRoutes, {
  SwipeableRouteChildrenProps,
} from "react-swipeable-routes";
import { Route, RouteComponentProps } from "react-router";
import AttestationContainer from "./AttestationOverview/AttestationContainer";
import NomenclatureContainer from "./NomenclatureCode/NomenclatureCodeContainer";
import PatientTypeContainer from "./PatientType/PatientTypeContainer";
import SwipeHome from "./SwipeHome";
import EFactEAttestContainer from "./EFactEAttest/EFactEAttestContainer";
import CoPaymentContainer from "./CoPayment/CoPaymentContainer";
import TimelineContainer from "./Timeline/TimelineContainer";
import PaymentContainer from "./Payment/PaymentContainer";
import ConsultationFinishedContainer from "./ConsultationFinished/ConsultationFinishedContainer";
import HamburgerViewModal from "./HamburgerMenu/HamburgerViewModal";
import { TarifiedItem } from "model/tarified-item.model";
import EditTarifiedItem from "main/Popups/TarifiedItemEdit";
import ErrorBoundary from "main/ErrorBoundary/ErrorBoundary";
import { History } from "history";
import MedicationCreate from "./MedicationOverview/MedicationCreate";
import MedicationOverviewContainer from "main/MedicationOverview/MedicationOverviewContainer";
import BackToStartContainer from "./ConsultationFinished/BackToStartContainer";
import { User } from "model/user.model";
import { Patient } from "model/patient.model";
import { DeleteOptionalTarificationSessionType } from "store/types/tarification.types";
import { GlobalState } from "store/configure-store";
import {
  selectEAttestAvailable,
  selectUser,
} from "store/reducers/user-context.reducer";
import {
  selectCt1,
  selectCt2,
  selectEFactEAttest,
  selectPatient,
} from "store/reducers/patient.reducer";
import { connect } from "react-redux";
import { deleteOptionalTarificationSession } from "store/actions/tarification.actions";
import { closePatient } from "store/actions/patient.actions";
import {
  ROUTE_CONSULTATION,
  ROUTE_MEDICATION,
  ROUTE_MEDICATION_CREATE,
  ROUTE_TARIFICATION,
  ROUTE_TARIFICATION_ATTEST,
  ROUTE_TARIFICATION_BACK,
  ROUTE_TARIFICATION_COPAY,
  ROUTE_TARIFICATION_EFACT,
  ROUTE_TARIFICATION_FINISH,
  ROUTE_TARIFICATION_NOM,
  ROUTE_TARIFICATION_PATIENT,
  ROUTE_TARIFICATION_PAYMENT,
  ROUTE_TARIFICATION_RESUME,
  ROUTE_TIMELINE,
} from "routes.global";
import Icon from "@cc/react-components/Icon";
import Navbar from "@cc/react-components/Navbar";
import { I18nextProvider } from "react-i18next";
import { i18nPatientContext } from "config/translations/I18n";
import { TarificationApplicationSession } from "model/tarification.model";
import {
  selectHelenaEnabled,
  selectHelenaPatientActive,
  selectHelenaPatientStatus,
  selectHelenaStatus,
} from "store/reducers/helena.reducer";
import { HelenaPatientStatusEnum, HelenaStatusEnum } from "model/helena.model";
import { EFactEAttestEnum } from "common/enums/EFactEAttestEnum";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import Page from "ui/Page";
import styled from "styled-components";
import HelenaPatientActivation from "main/HelenaInvite/HelenaPatientActivation";

export interface Props
  extends React.HTMLAttributes<HTMLElement>,
    RouteComponentProps {}

const renderAttestationContainer = (
  onEditTarifiedItem: (t: TarifiedItem) => void,
  history: History,
  inView: boolean
) => {
  return (
    <AttestationContainer
      key={"AttestationContainer"}
      inView={inView}
      onEditTarifiedItem={onEditTarifiedItem}
      history={history}
    />
  );
};

const renderConsultationFinishedContainer = ({
  match,
}: SwipeableRouteChildrenProps) => {
  return <ConsultationFinishedContainer inView={match.type === "full"} />;
};

const renderBackToStartContainer = ({ match }: SwipeableRouteChildrenProps) => {
  return <BackToStartContainer inView={match.type === "full"} />;
};

const renderTimeline = ({ match }: SwipeableRouteChildrenProps) => {
  return (
    <I18nextProvider i18n={i18nPatientContext}>
      <TimelineContainer inView={match.type === "full"} />
    </I18nextProvider>
  );
};

const renderMedicationOverview = ({ match }: SwipeableRouteChildrenProps) => {
  return (
    <I18nextProvider i18n={i18nPatientContext}>
      <MedicationOverviewContainer inView={match.type === "full"} />
    </I18nextProvider>
  );
};

const renderMedicationCreate = ({ match }: SwipeableRouteChildrenProps) => {
  return <MedicationCreate inView={match.type === "full"} />;
};

const ConsultationFlow = ({
  user,
  patient,
  closePatient,
  location,
  history,
  tarificationSession,
  tarificationSessionKey,
  deleteOptionalTarificationSession,
  selectedMedicationEntries,
  tarificationPossible,
  helenaPatientActive,
  helenaEnabled,
  helenaStatus,
}: StateToPropsState & DispatchToPropsState & Props) => {
  /**
   * TimelineItem
   */
  const [scrollableScreen, setScrollableScreen] = useState(true);

  useEffect(() => {
    const isScrollablePath = (pathname: string): boolean => {
      return (
        pathname.includes(ROUTE_TIMELINE) || pathname.includes(ROUTE_MEDICATION)
      );
    };
    setScrollableScreen(isScrollablePath(location.pathname));
  }, [location.pathname, history]);

  /**
   *   Tarified Item Popup
   */
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [previousTarificationStep, setTarificationStep] = useState(
    ROUTE_TARIFICATION_NOM
  );
  useEffect(() => {
    const path = location.pathname;
    if (path === ROUTE_TARIFICATION_RESUME) {
      history.replace(previousTarificationStep);
    } else if (path && path.startsWith(ROUTE_TARIFICATION)) {
      setTarificationStep(path);
    }
  }, [
    location.pathname,
    history,
    previousTarificationStep,
    setTarificationStep,
  ]);

  useEffect(() => {
    if (location.pathname === ROUTE_CONSULTATION) {
      history.replace(ROUTE_TIMELINE);
    }
  }, [location.pathname, history]);

  const [consultationRoutes, setRoutes] = useState<JSX.Element[]>([]);
  useEffect(() => {
    setRoutes(
      getRoutes(
        selectedMedicationEntries,
        tarificationPossible,
        onEditTarifiedItem
      )
    );
  }, [
    selectedMedicationEntries,
    tarificationPossible,
    helenaEnabled,
    helenaPatientActive,
    helenaStatus,
  ]);

  const [isModalTarifiedItemOpen, setModalTarifiedItemOpen] = useState(true);
  const [
    selectedTarifiedItem,
    setSelectedTarifiedItem,
  ] = useState<TarifiedItem>();

  const onEditTarifiedItem = (tarifiedItem: TarifiedItem) => {
    setSelectedTarifiedItem(tarifiedItem);
    setModalTarifiedItemOpen(true);
  };

  const renderEditTarifiedItemModal = () => {
    return (
      selectedTarifiedItem && (
        <EditTarifiedItem
          isModalOpen={isModalTarifiedItemOpen}
          setModalOpen={setModalTarifiedItemOpen}
          tarifiedItem={selectedTarifiedItem}
        />
      )
    );
  };

  /**
   * Hamburger menu popup
   */
  const renderHamburgerMenu = () => {
    return (
      <HamburgerViewModal
        show={show}
        onHide={handleClose}
        user={user}
        patient={patient}
        tarificationSession={tarificationSession}
        tarificationSessionKey={tarificationSessionKey}
      />
    );
  };

  /**
   * Stepper
   */
  const renderStepper = () => {
    const routeOptions = [
      ROUTE_TARIFICATION_NOM,
      ROUTE_TARIFICATION_PATIENT,
      ROUTE_TARIFICATION_EFACT,
      ROUTE_TARIFICATION_COPAY,
      ROUTE_TARIFICATION_ATTEST,
      ROUTE_TARIFICATION_PAYMENT,
    ];

    return (
      !scrollableScreen &&
      routeOptions.findIndex((it) => it === location.pathname) > -1 && (
        <div className="text-center fixed-bottom">
          {routeOptions.map((route: string) => {
            return route === location.pathname ? (
              <span className="pl-1 pr-1" key={route}>
                <SelectedDot />
              </span>
            ) : (
              <span className="pl-1 pr-1" key={route}>
                <Dot />
              </span>
            );
          })}
        </div>
      )
    );
  };

  const renderMenuIcon = () => {
    return (
      <Icon type="menu" size="2x" className="text-white" onClick={handleShow} />
    );
  };

  return (
    <React.Fragment>
      {renderEditTarifiedItemModal()}
      {renderHamburgerMenu()}
      <Navbar>
        <PatientInfo avatar variant="header" patient={patient} />
        {renderMenuIcon()}
      </Navbar>
      <Page className="p-0">
        <ErrorBoundary>
          <HelenaPatientActivation>
            <SwipeHome
              enableSwipe={!scrollableScreen}
              onSwipeHome={() => {
                deleteOptionalTarificationSession(
                  tarificationSession,
                  user.id,
                  patient.id,
                  tarificationSessionKey
                );
                closePatient();
              }}
            >
              <SwipeableRoutes
                containerStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexGrow: 1,
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  height: "100%",
                  overflow: "hidden",
                }}
                slideStyle={{ position: "relative" }}
                enableMouseEvents
                replace
                ignoreNativeScroll={false}
              >
                {consultationRoutes}
              </SwipeableRoutes>
            </SwipeHome>
          </HelenaPatientActivation>
        </ErrorBoundary>
      </Page>

      {renderStepper()}
    </React.Fragment>
  );
};

const getRoutes = (
  selectedMedicationEntries: Array<number>,
  tarificationPossible: boolean,
  onEditTarifiedItem: (t: TarifiedItem) => void
) => {
  const routes = [];
  routes.push(
    <Route
      path={ROUTE_TIMELINE}
      key={ROUTE_TIMELINE}
      component={renderTimeline}
    />
  );
  routes.push(
    <Route
      path={ROUTE_MEDICATION}
      key={ROUTE_MEDICATION}
      component={renderMedicationOverview}
    />
  );
  if (selectedMedicationEntries?.length > 0) {
    routes.push(
      <Route
        path={ROUTE_MEDICATION_CREATE}
        key={ROUTE_MEDICATION_CREATE}
        component={renderMedicationCreate}
      />
    );
  }
  routes.push(
    <Route
      path={ROUTE_TARIFICATION_NOM}
      key={ROUTE_TARIFICATION_NOM}
      component={NomenclatureContainer}
    />
  );
  routes.push(
    <Route
      path={ROUTE_TARIFICATION_PATIENT}
      key={ROUTE_TARIFICATION_PATIENT}
      component={PatientTypeContainer}
    />
  );
  routes.push(
    <Route
      path={ROUTE_TARIFICATION_EFACT}
      key={ROUTE_TARIFICATION_EFACT}
      component={EFactEAttestContainer}
    />
  );
  if (tarificationPossible) {
    routes.push(
      <Route
        path={ROUTE_TARIFICATION_COPAY}
        key={ROUTE_TARIFICATION_COPAY}
        component={CoPaymentContainer}
      />
    );
    routes.push(
      <Route
        path={ROUTE_TARIFICATION_ATTEST}
        key={ROUTE_TARIFICATION_ATTEST}
        render={(props: any) =>
          renderAttestationContainer(
            onEditTarifiedItem,
            props.history,
            props.match.type === "full"
          )
        }
      />
    );
    routes.push(
      <Route
        path={ROUTE_TARIFICATION_PAYMENT}
        key={ROUTE_TARIFICATION_PAYMENT}
        component={PaymentContainer}
      />
    );
    routes.push(
      <Route
        path={ROUTE_TARIFICATION_FINISH}
        key={ROUTE_TARIFICATION_FINISH}
        component={renderConsultationFinishedContainer}
      />
    );
  }
  if (tarificationPossible) {
    routes.push(
      <Route
        path={ROUTE_TARIFICATION_BACK}
        key={ROUTE_TARIFICATION_BACK}
        component={renderBackToStartContainer}
      />
    );
  }
  return routes;
};

const isEFactOrEAttestAvailable = (state: GlobalState): boolean => {
  const eAttestAvailable = selectEAttestAvailable(state);
  const eFactEAttest = selectEFactEAttest(state);
  const patientHasInsurability = !!(selectCt1(state) ?? selectCt2(state));
  return (
    eAttestAvailable ||
    (eFactEAttest === EFactEAttestEnum.EFact && patientHasInsurability)
  );
};

const Dot = styled.div`
  background-color: #d4d4d5;
  height: 0.5rem;
  width: 0.5rem;
  border-radius: 50%;
  display: inline-block;
`;

const SelectedDot = styled.div`
  background-color: #9a9a9b;
  height: 0.5rem;
  width: 0.5rem;
  border-radius: 50%;
  display: inline-block;
`;

interface StateToPropsState {
  user: User;
  patient: Patient;
  tarificationSession: TarificationApplicationSession;
  tarificationSessionKey: TarificationSessionKey | undefined;
  selectedMedicationEntries: Array<number>;
  helenaStatus: HelenaStatusEnum;
  helenaPatientStatus: HelenaPatientStatusEnum;
  helenaEnabled: boolean;
  helenaPatientActive: boolean;
  tarificationPossible: boolean;
}

interface DispatchToPropsState {
  closePatient(): void;
  deleteOptionalTarificationSession: DeleteOptionalTarificationSessionType;
}

const mapStateToProps = (state: GlobalState): StateToPropsState => {
  return {
    user: selectUser(state),
    patient: selectPatient(state),
    tarificationSession: state.tarification.tarificationApplicationSession,
    tarificationSessionKey: state.patientContext.tarificationSession,
    selectedMedicationEntries:
      state.medicationContext.selectedMedicationEntries,
    tarificationPossible: isEFactOrEAttestAvailable(state),
    helenaStatus: selectHelenaStatus(state.helena),
    helenaPatientStatus: selectHelenaPatientStatus(state.helena),
    helenaEnabled: selectHelenaEnabled(state.helena),
    helenaPatientActive: selectHelenaPatientActive(state.helena),
  };
};

export default connect<
  StateToPropsState,
  DispatchToPropsState,
  Props,
  GlobalState
>(mapStateToProps, {
  closePatient,
  deleteOptionalTarificationSession,
})(ConsultationFlow);
